import { toNDigits } from '@mail/cross-sizes-utils';
import classNames from 'clsx';
import { ReactComponent as WarningIcon } from 'img/icons/uploader/warning.svg';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { getFileName, showInfoIcon } from 'reactApp/modules/uploadList/uploadList.getters';
import { type IInputFile, EFileError } from 'reactApp/modules/uploadList/uploadList.model';
import { getUserFileSizeLimit } from 'reactApp/modules/uploadList/uploadList.selectors';
import { getErrorInfo } from 'reactApp/modules/uploadList/uploadList.utils';
import FilePicture from 'reactApp/ui/FilePicture/FilePicture';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './Problems.css';

interface IProps {
    file: IInputFile;
}

export const InfoErrorComponent = memo(({ file }: IProps) => {
    const userFileSizeLimit = toNDigits({
        bytes: useSelector(getUserFileSizeLimit),
        maxDigitsCount: 3,
    }).value;
    const error = file.error || EFileError.UNKNOWN;
    const errorInfo = getErrorInfo(error, {
        file,
        userFileSizeLimit,
        fileName: getFileName(file),
        folderName: file?.localPath?.split(`/${file.name}`)[0],
    });
    const isInfoIcon = showInfoIcon(file);

    return (
        <div className={classNames(styles.problem, { [styles[`problem_${errorInfo.mode}`]]: !!errorInfo.mode })}>
            <div className={styles.icon}>
                {isInfoIcon ? (
                    errorInfo.icon ?? (
                        <FilePicture
                            icon={file.extension}
                            file={file}
                            thumbUrl={file.thumb}
                            borderRadius={true}
                            removeLoader={() => true}
                            loadThumb={() => true}
                        />
                    )
                ) : (
                    <WarningIcon />
                )}
            </div>
            <div className={classNames({ [styles.wrapperText]: true })}>
                <div>{errorInfo.text}</div>
                {!!errorInfo.description && <div>{errorInfo.description}</div>}
                {(!!errorInfo.link?.url || !!errorInfo.link?.onClick) && (
                    <div className={styles.infoLink}>
                        <a
                            href={errorInfo.link?.url ?? undefined}
                            rel="noopener noreferrer"
                            target="_blank"
                            onClick={errorInfo.link.onClick ?? noopVoid}
                            role={errorInfo.link?.url ? 'button' : undefined}
                        >
                            {errorInfo.link.text}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
});

InfoErrorComponent.displayName = 'InfoErrorComponent';
